// src/app/pages/orders/CreateOrderModal.js

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, createFilterOptions,
  Grid, Button, TextField, Typography, Stack, Box, InputAdornment, CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import _ from 'lodash';
import customerServices from 'app/services/customer-services';
import sellerServices from 'app/services/seller-services';
import centreServices from 'app/services/centre-services';
import orderServices from 'app/services/order-services';

// Cada lista muestra las primeras 10 y la lupa busca una especifica. Clientes y productos se
// buscan en el backend (pueden ser miles); vendedores y bodegas son pocos y se filtran aca.
// Clientes y vendedores ademas traen 10 mas al llegar al final de la lista.
const LIMIT = 10;
const firstTen = createFilterOptions({ limit: LIMIT });
const filterByLabel = createFilterOptions();

// Pide mas cuando el scroll de la lista llega cerca del fondo. El Autocomplete vuelve la
// lista arriba cada vez que cambian las opciones, asi que se guarda donde iba al pedir y se
// devuelve ahi cuando llegan (`count`: cuantas opciones hay). `onBottom` dice si pidio algo.
// `toTop` es para una busqueda nueva: si la lista quedaba abajo, al achicarse pediria mas.
const useInfiniteListbox = (onBottom, count) => {
  const pending = useRef(null);
  const listbox = useRef(null);

  useEffect(() => {
    const p = pending.current;
    if (!p) return;
    pending.current = null;
    if (p.el.isConnected) p.el.scrollTop = p.top;
  }, [count]);

  const toTop = () => {
    pending.current = null;
    if (listbox.current?.isConnected) listbox.current.scrollTop = 0;
  };

  const onScroll = (e) => {
    const el = e.currentTarget;
    listbox.current = el;
    // Con 10 opciones la lista casi entra entera: estando arriba ya queda a menos de 40px del
    // fondo, asi que solo cuenta si de verdad bajo.
    if (el.scrollTop === 0 || el.scrollTop + el.clientHeight < el.scrollHeight - 40) return;
    if (onBottom()) pending.current = { el, top: el.scrollTop };
  };

  return { props: { onScroll }, toTop };
};

const searchInput = (params, label, loading = false) => (
  <TextField
    {...params}
    label={label}
    InputProps={{
      ...params.InputProps,
      startAdornment: (
        <>
          <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>
          {params.InputProps.startAdornment}
        </>
      ),
      endAdornment: (
        <>
          {loading && <CircularProgress color="inherit" size={18} />}
          {params.InputProps.endAdornment}
        </>
      ),
    }}
  />
);

// Opciones que vienen del backend segun lo escrito, con un respiro para no pedir por tecla.
const useRemoteOptions = (fetcher, open) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = useMemo(() => _.debounce((q, done) => {
    fetcher(q).then(done).catch(() => done([]));
  }, 300), [fetcher]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    search(input, (result) => {
      if (!active) return;
      setOptions(result || []);
      setLoading(false);
    });
    return () => { active = false; };
  }, [input, open, search]);

  return { options, loading, setInput };
};

// Como useRemoteOptions, pero de a LIMIT: `loadMore` agrega la pagina siguiente. Escribir en
// la lupa vuelve a la primera pagina.
const usePagedRemoteOptions = (fetchPage, open) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  // Cuenta las busquedas para descartar una pagina que llega despues de que cambio el texto.
  const requestId = useRef(0);

  const search = useMemo(() => _.debounce((q, id) => {
    fetchPage(q, 0)
      .then((result) => {
        if (id !== requestId.current) return;
        const list = result || [];
        setOptions(list);
        setHasMore(list.length === LIMIT);
      })
      .catch(() => { if (id === requestId.current) { setOptions([]); setHasMore(false); } })
      .finally(() => { if (id === requestId.current) setLoading(false); });
  }, 300), [fetchPage]);

  useEffect(() => {
    if (!open) return;
    requestId.current += 1;
    setLoading(true);
    search(input, requestId.current);
  }, [input, open, search]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return false;
    const id = requestId.current;
    setLoading(true);
    fetchPage(input, options.length)
      .then((result) => {
        if (id !== requestId.current) return;
        const list = result || [];
        // Sin repetidos por si el backend no respeta el offset.
        setOptions((prev) => _.uniqBy([...prev, ...list], 'id'));
        setHasMore(list.length === LIMIT);
      })
      .catch(() => { if (id === requestId.current) setHasMore(false); })
      .finally(() => { if (id === requestId.current) setLoading(false); });
    return true;
  }, [fetchPage, hasMore, input, loading, options.length]);

  return { options, loading, setInput, loadMore };
};

const fetchCustomers = (q, offset) => customerServices.searchByCompany({ q: q || undefined, limit: LIMIT, offset });
const fetchProducts = (q) => orderServices.getProductOptions(q);

const CreateOrderModal = ({
  open,
  onClose,
  onSubmit,
  newOrder,
  setNewOrder,
  newProduct,
  setNewProduct
}) => {
  const [sellers, setSellers] = useState([]);
  const [centres, setCentres] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const customers = usePagedRemoteOptions(fetchCustomers, open);
  // Los vendedores llegan todos de una; el scroll solo va mostrando de a LIMIT.
  const [sellerCount, setSellerCount] = useState(LIMIT);
  const products = useRemoteOptions(fetchProducts, open);
  const customerListbox = useInfiniteListbox(customers.loadMore, customers.options.length);
  const sellerListbox = useInfiniteListbox(
    () => {
      if (sellerCount >= sellers.length) return false;
      setSellerCount(sellerCount + LIMIT);
      return true;
    },
    sellerCount,
  );
  // Texto del buscador de producto, controlado para poder vaciarlo al agregar la linea.
  const [productInput, setProductInput] = useState('');

  useEffect(() => {
    if (!open) return;
    setProductInput('');
    setSellerCount(LIMIT);
    sellerServices.search().then((data) => setSellers(data || [])).catch(() => setSellers([]));
    centreServices.searchByCompany().then((data) => {
      const list = data || [];
      setCentres(list);
      // Con una sola bodega no hay nada que elegir.
      if (list.length === 1 && !newOrder.centreId) setNewOrder((prev) => ({ ...prev, centreId: list[0].id }));
    }).catch(() => setCentres([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const quantity = parseFloat(newProduct.quantity);
  const canAddProduct = Boolean(newProduct.name) && quantity > 0;

  const handleAddProduct = () => {
    if (!canAddProduct) return;
    setNewOrder({ ...newOrder, products: [...newOrder.products, { ...newProduct, quantity }] });
    setNewProduct({ code: null, name: '', unit: null, quantity: '' });
    setProductInput('');
  };

  const handleRemoveProduct = (index) => {
    const updated = newOrder.products.filter((_p, i) => i !== index);
    setNewOrder({ ...newOrder, products: updated });
  };

  const selectedCustomer = newOrder.customer || null;
  const selectedSeller = _.find(sellers, { id: newOrder.sellerId }) || null;
  const selectedCentre = _.find(centres, { id: newOrder.centreId }) || null;
  const selectedProduct = newProduct.name ? { code: newProduct.code, name: newProduct.name, unit: newProduct.unit } : null;

  const missing = [
    !newOrder.customerId && 'un cliente',
    !newOrder.centreId && 'una bodega',
    newOrder.products.length === 0 && 'al menos un producto',
  ].filter(Boolean);
  const canCreate = missing.length === 0 && !submitting;

  const handleSubmit = async () => {
    if (!canCreate) return;
    setSubmitting(true);
    try {
      await onSubmit();
    } finally {
      setSubmitting(false);
    }
  };

  const sellerName = (s) => [s?.first_name, s?.last_name].filter(Boolean).join(' ') || s?.name || s?.email || `Vendedor ${s?.id}`;
  const productLabel = (p) => (typeof p === 'string' ? p : [p?.code, p?.name].filter(Boolean).join(' · '));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { width: '800px', maxWidth: '90%' } }}
    >
      <DialogTitle>
        <Typography variant="h2" color="primary">Crear nuevo pedido</Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              options={customers.options}
              loading={customers.loading}
              value={selectedCustomer}
              // El backend ya filtra: aca no se vuelve a filtrar sobre lo que devolvio.
              filterOptions={(x) => x}
              getOptionLabel={(c) => c?.name || ''}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              renderOption={(props, c) => (
                <li {...props} key={c.id}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <StorefrontOutlinedIcon fontSize="small" color="action" />
                    <Stack>
                      <Typography variant="body2">{c.name}</Typography>
                      {c.code && <Typography variant="caption" color="text.secondary">{c.code}</Typography>}
                    </Stack>
                  </Stack>
                </li>
              )}
              onInputChange={(_e, value, reason) => {
                if (reason === 'reset') return;
                customerListbox.toTop();
                customers.setInput(value);
              }}
              ListboxProps={customerListbox.props}
              onChange={(_e, c) => setNewOrder({ ...newOrder, customer: c, customerId: c?.id || null })}
              noOptionsText="No hay clientes con ese nombre"
              renderInput={(params) => searchInput(params, 'Cliente *', customers.loading)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={sellers}
              value={selectedSeller}
              filterOptions={(list, state) => filterByLabel(list, state).slice(0, sellerCount)}
              getOptionLabel={sellerName}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              renderOption={(props, sl) => (
                <li {...props} key={sl.id}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <PersonOutlineOutlinedIcon fontSize="small" color="action" />
                    <Typography variant="body2">{sellerName(sl)}</Typography>
                  </Stack>
                </li>
              )}
              onInputChange={(_e, _value, reason) => {
                if (reason !== 'input') return;
                sellerListbox.toTop();
                setSellerCount(LIMIT);
              }}
              onOpen={() => setSellerCount(LIMIT)}
              ListboxProps={sellerListbox.props}
              onChange={(_e, s) => setNewOrder({ ...newOrder, sellerId: s?.id || null })}
              noOptionsText={sellers.length ? 'No hay vendedores con ese nombre' : 'La compañía no tiene vendedores'}
              renderInput={(params) => searchInput(params, 'Vendedor')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={centres}
              value={selectedCentre}
              filterOptions={firstTen}
              getOptionLabel={(c) => c?.name || ''}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              onChange={(_e, c) => setNewOrder({ ...newOrder, centreId: c?.id || null })}
              noOptionsText="No hay bodegas con ese nombre"
              renderInput={(params) => searchInput(params, 'Bodega *')}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Fecha de despacho"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={newOrder.creationDate}
              onChange={(e) => setNewOrder({ ...newOrder, creationDate: e.target.value })}
            />
          </Grid>

          <Grid item xs={12} sm={7}>
            <Autocomplete
              // Libre: una compañia sin catalogo ni pedidos anteriores igual puede escribir el producto.
              freeSolo
              options={products.options}
              loading={products.loading}
              value={selectedProduct}
              inputValue={productInput}
              filterOptions={(x) => x}
              getOptionLabel={productLabel}
              isOptionEqualToValue={(a, b) => a?.code === b?.code && a?.name === b?.name}
              renderOption={(props, p) => (
                <li {...props} key={`${p.code}-${p.name}`}>
                  <Stack>
                    <Typography variant="body2">{p.name}</Typography>
                    {p.code && <Typography variant="caption" color="text.secondary">{p.code}{p.unit ? ` · ${p.unit}` : ''}</Typography>}
                  </Stack>
                </li>
              )}
              onInputChange={(_e, value, reason) => {
                setProductInput(value);
                if (reason === 'reset') return;
                products.setInput(value);
                // Escrito a mano, sin elegir de la lista: queda sin codigo.
                setNewProduct({ ...newProduct, code: null, unit: null, name: value });
              }}
              onChange={(_e, p) => {
                if (!p) return setNewProduct({ ...newProduct, code: null, name: '', unit: null });
                if (typeof p === 'string') return setNewProduct({ ...newProduct, code: null, name: p, unit: null });
                return setNewProduct({ ...newProduct, code: p.code, name: p.name, unit: p.unit });
              }}
              noOptionsText="No hay productos con ese código o nombre"
              renderInput={(params) => searchInput(params, 'Producto', products.loading)}
            />
          </Grid>

          <Grid item xs={8} sm={3}>
            <TextField
              label="Cantidad"
              type="number"
              fullWidth
              inputProps={{ min: 0 }}
              value={newProduct.quantity}
              onChange={(e) => setNewProduct({ ...newProduct, quantity: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddProduct(); }}
            />
          </Grid>

          <Grid item xs={4} sm={2}>
            <Button variant="outlined" color="primary" fullWidth sx={{ height: '100%' }} disabled={!canAddProduct} onClick={handleAddProduct}>
              Agregar
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Stack direction="column" spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #000', py: 1, fontWeight: 'bold', px: 1 }}>
                <Box sx={{ flex: 2 }}><Typography variant="body2">Producto</Typography></Box>
                <Box sx={{ flex: 1 }}><Typography variant="body2">Cantidad</Typography></Box>
                <Box sx={{ width: 80 }} />
                <Box sx={{ width: 80 }} />
              </Box>

              {newOrder.products.length === 0 &&
                <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                  Busca un producto, indica la cantidad y presiona Agregar.
                </Typography>
              }

              {newOrder.products.map((p, index) => (
                <Box
                  key={index}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #ddd', py: 1, px: 1 }}
                >
                  <Box sx={{ flex: 2, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>{p.name}</Typography>
                    {p.code && <Typography variant="caption" color="text.secondary">{p.code}</Typography>}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">{p.quantity}{p.unit ? ` ${p.unit}` : ''}</Typography>
                  </Box>
                  <Box sx={{ width: 80 }}>
                    <Button
                      size="small" color="primary" variant="outlined" fullWidth
                      onClick={() => { setNewProduct({ code: p.code, name: p.name, unit: p.unit, quantity: p.quantity }); setProductInput(productLabel(p)); handleRemoveProduct(index); }}
                    >
                      Editar
                    </Button>
                  </Box>
                  <Box sx={{ width: 80 }}>
                    <Button size="small" color="error" fullWidth onClick={() => handleRemoveProduct(index)}>
                      Eliminar
                    </Button>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3 }}>
        {missing.length > 0 &&
          <Typography variant="caption" color="warning.main" sx={{ mr: 'auto' }}>
            Falta {missing.join(', ')}.
          </Typography>
        }
        <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
        <Button variant="contained" color="success" disabled={!canCreate} onClick={handleSubmit}>
          {submitting ? 'Creando…' : 'Crear pedido'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateOrderModal;
